import { InventoryRepository } from '@lib/api/repository/inventory/inventory.repository';
import {
  AdminInventoryItem,
  InventoryInput,
  OwnerInventoryFilters,
} from '@lib/types';
import { AdminPage, AdminPageFilters } from '@lib/types/admin-page';

import { Requester } from '../clients';

import {
  AdminInventoryItemDTO,
  InventoryItemDTO,
  OwnerInventoryItemDTO,
  OwnerInventoryPageDTO,
  createAdminInventoryItem,
  createInventoryItems,
  createOwnerInventoryItem,
  createOwnerInventoryPage,
} from './models';

export default class InventoryAPI implements InventoryRepository {
  async listAdminPage(
    filters: AdminPageFilters,
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminInventoryItem>> {
    const result = (
      await Requester.get<AdminPage<AdminInventoryItemDTO>>(
        '/marketplace/admin/inventory/page',
        { params: { ...filters }, signal },
      )
    ).data;

    return {
      items: result.items.map(createAdminInventoryItem),
      total: result.total,
    };
  }

  async listByStorePage(
    slug: string,
    filters?: Record<string, string>,
    signal?: AbortSignal,
  ) {
    const { data } = await Requester.get<{
      items: InventoryItemDTO[];
      nextCursor?: string;
      totalItems: number;
    }>(`/marketplace/stores/${encodeURIComponent(slug)}/offers/page`, {
      signal,
      params: filters,
    });

    return { ...data, items: createInventoryItems(data.items) };
  }

  async listByStore(
    slug: string,
    filters?: Record<string, string>,
    signal?: AbortSignal,
  ) {
    const response = (
      await Requester.get<OwnerInventoryItemDTO[]>(
        `/marketplace/stores/${slug}/offers`,
        { signal, params: filters },
      )
    ).data;

    return createInventoryItems(response);
  }

  async listMine(filters?: OwnerInventoryFilters, signal?: AbortSignal) {
    const response = (
      await Requester.get<OwnerInventoryPageDTO>('/marketplace/inventory/me', {
        signal,
        params: filters ? { ...filters } : undefined,
      })
    ).data;

    return createOwnerInventoryPage(response);
  }

  async listAdmin(signal?: AbortSignal) {
    const response = await Requester.get<AdminInventoryItemDTO[]>(
      '/marketplace/admin/inventory',
      { signal },
    );

    return response.data.map(createAdminInventoryItem);
  }

  async create(input: InventoryInput) {
    const response = (
      await Requester.post<OwnerInventoryItemDTO>('/marketplace/inventory', {
        body: input,
      })
    ).data;

    return createOwnerInventoryItem(response);
  }

  async createBatch(items: InventoryInput[]) {
    const response = (
      await Requester.post<OwnerInventoryItemDTO[]>(
        '/marketplace/inventory/batch',
        { body: { items }, customTimeout: 360_000 },
      )
    ).data;

    return response.map(createOwnerInventoryItem);
  }

  async update(id: string, input: InventoryInput) {
    const response = (
      await Requester.patch<OwnerInventoryItemDTO>(
        `/marketplace/inventory/${id}`,
        { body: input },
      )
    ).data;

    return createOwnerInventoryItem(response);
  }

  async updateStock(id: string, quantity: number) {
    const response = (
      await Requester.patch<OwnerInventoryItemDTO>(
        `/marketplace/inventory/${id}/stock`,
        { body: { quantity } },
      )
    ).data;

    return createOwnerInventoryItem(response);
  }

  async remove(id: string) {
    await Requester.delete(`/marketplace/inventory/${id}`);
  }

  async removeByGame(gameCode: string) {
    const response = (
      await Requester.delete<{ deletedCount: number }>(
        `/marketplace/inventory/game/${encodeURIComponent(gameCode)}`,
      )
    ).data;

    return response;
  }

  async setActive(id: string, active: boolean) {
    const response = (
      await Requester.patch<AdminInventoryItemDTO>(
        `/marketplace/admin/inventory/${id}/${active}`,
      )
    ).data;

    return createAdminInventoryItem(response);
  }

  async setMineActive(id: string, active: boolean) {
    const response = (
      await Requester.patch<OwnerInventoryItemDTO>(
        `/marketplace/inventory/${id}/active/${active}`,
      )
    ).data;

    return createOwnerInventoryItem(response);
  }
}
